import React from "react";

import { css, cx } from "@emotion/css";
import { Group } from "@visx/group";

import { useStyles } from "@/hooks/useStyles.ts";
import { DayData } from "@/models/CGMDailyGraphData.ts";
import {
  ClosedLoopPumpMode,
  ClosedLoopPumpModeChangeEvent,
  ClosedLoopUserMode,
  ClosedLoopUserModeChangeEvent,
} from "@/models/InsulinPumpEvents.ts";
import { useTheme, Theme } from "@/theme/CreateTheme.ts";
import {
  ClosedLoopPumpModeTooltip,
  ClosedLoopUserModeTooltip,
} from "@/uiKit/molecules/graphs/InsulinGraph/InsulinTooltips.tsx";
import { toDateTime } from "@/utils/dateUtils.ts";
import { SvgLayout, SvgRect, boundedDateTimeToSecond, dayScale } from "@/utils/graphUtils.ts";

type ClosedLoopModeGraphLayout = {
  container: SvgLayout;
  pumpModeArea: SvgLayout;
  userModeArea: SvgLayout;
};

export type ClosedLoopModeGraphProps = {
  date: string;
  pumpModes: DayData<ClosedLoopPumpModeChangeEvent>;
  userModes: DayData<ClosedLoopUserModeChangeEvent>;
  className?: string;
  paddingTop?: number;
} & SvgLayout;

const ClosedLoopModeGraph_: React.FC<ClosedLoopModeGraphProps> = ({
  date,
  pumpModes,
  userModes,
  className,
  paddingTop = 0,
  ...dim
}) => {
  const styles = useStyles(makeStyles);
  const theme = useTheme();
  const layout = getLayout(dim, paddingTop);

  const xScale = dayScale(layout.container.width);
  const day = toDateTime(date);

  const pumpModesData = getEventData(pumpModes);

  // Convert data to x, y coordinates
  const pumpModesCoordData = pumpModesData.map(item => ({
    data: item,
    x: xScale(boundedDateTimeToSecond(day, item.date)),
    y: 0,
  }));

  const pumpModeRects: (SvgRect & {
    data: {
      id: string;
      date: string;
      endDate: string;
      mode: ReducedPumpMode;
      previousMode?: ReducedPumpMode;
    };
  })[] = [];
  for (let i = 0; i < pumpModesCoordData.length - 1; i++) {
    const item = pumpModesCoordData[i];
    const nextItem = pumpModesCoordData[i + 1];
    const rect = {
      x: item.x,
      y: item.y,
      width: nextItem.x - item.x,
      height: layout.pumpModeArea.height,
      data: {
        id: item.data.id,
        date: item.data.date,
        endDate: nextItem.data.date,
        mode: pumpModeMapping(item.data.data.mode),
        previousMode: item.data.data.previousMode ? pumpModeMapping(item.data.data.previousMode) : undefined,
      },
    };
    const previousRect = pumpModeRects[pumpModeRects.length - 1];
    if (previousRect && previousRect.data.mode === rect.data.mode) {
      previousRect.width += rect.width;
      previousRect.data.endDate = nextItem.data.date;
    } else {
      pumpModeRects.push(rect);
    }
  }

  const userModesData = getEventData(userModes);

  const userModesCoordData = userModesData.map(item => ({
    data: item,
    x: xScale(boundedDateTimeToSecond(day, item.date)),
    y: 0,
  }));

  const userModesRects: (SvgRect & {
    data: {
      id: string;
      date: string;
      endDate: string;
      mode: ClosedLoopUserMode;
    };
  })[] = [];

  for (let i = 0; i < userModesCoordData.length - 1; i++) {
    if (userModesCoordData[i].data.data.mode !== "normal") {
      const item = userModesCoordData[i];
      const nextItem = userModesCoordData[i + 1];
      const rect = {
        x: item.x,
        y: item.y,
        width: nextItem.x - item.x,
        height: layout.pumpModeArea.height,
        data: {
          id: item.data.id,
          date: item.data.date,
          endDate: nextItem.data.date,
          mode: item.data.data.mode,
        },
      };
      userModesRects.push(rect);
    }
  }

  return (
    <Group className={cx(styles.container, className)} {...layout.container}>
      <Group {...layout.pumpModeArea}>
        {pumpModeRects.map(item => (
          <ClosedLoopPumpModeTooltip
            key={item.data.id}
            pumpMode={item.data.mode}
            date={item.data.date}
            endDate={item.data.endDate}
          >
            <rect
              key={item.data.id}
              {...item}
              fill={`color-mix(in srgb, ${getPumpModeColor(theme, item.data.mode)} 70%, white 30%)`}
              stroke={getPumpModeColor(theme, item.data.mode)}
              strokeWidth={2}
              rx={4}
              ry={2}
            />
          </ClosedLoopPumpModeTooltip>
        ))}
      </Group>
      <Group {...layout.userModeArea}>
        {userModesRects.map(item => (
          <ClosedLoopUserModeTooltip
            key={item.data.id}
            mode={item.data.mode}
            date={item.data.date}
            endDate={item.data.endDate}
          >
            <rect
              key={item.data.id}
              {...item}
              fill={`color-mix(in srgb, ${getUserModeColor(theme, item.data.mode)} 70%, white 30%)`}
              stroke={getUserModeColor(theme, item.data.mode)}
              strokeWidth={2}
              rx={4}
              ry={2}
            />
          </ClosedLoopUserModeTooltip>
        ))}
      </Group>
    </Group>
  );
};

export const ClosedLoopModeGraph = React.memo(ClosedLoopModeGraph_);

const getLayout = (dim: SvgLayout, verticalPadding: number): ClosedLoopModeGraphLayout => {
  const totalPadding = verticalPadding * 3;
  const contentHeight = (dim.height - totalPadding) / 2;
  return {
    container: dim,
    pumpModeArea: {
      top: verticalPadding,
      left: 0,
      width: dim.width,
      height: contentHeight,
    },
    userModeArea: {
      top: contentHeight + verticalPadding * 2,
      left: 0,
      width: dim.width,
      height: contentHeight,
    },
  };
};

type EventData = {
  id: string;
  date: string;
  data: {
    mode: string;
    previousMode?: string;
  };
};

const getEventData = <T extends EventData>(dayData: DayData<T>) => {
  const orderedData = dayData.data.toReversed();
  if (dayData.firstOfNextDay) {
    orderedData.push(dayData.firstOfNextDay);
  }
  if (orderedData.length < 1) {
    return orderedData;
  }
  const firstItem = orderedData[0];
  const startOfDay = toDateTime(firstItem.date).startOf("day");
  if (firstItem.data.previousMode) {
    return [
      {
        ...firstItem,
        date: startOfDay,
        data: {
          ...firstItem.data,
          mode: firstItem.data.previousMode,
          previousMode: undefined,
        },
      },
      ...orderedData,
    ];
  }
  return orderedData;
};

type ReducedPumpMode = "closed_loop" | "manual";

const pumpModeMapping = (mode: ClosedLoopPumpMode): ReducedPumpMode => {
  switch (mode) {
    case "closed_loop":
      return "closed_loop";
    case "no_control":
    case "open_loop":
    case "pining":
      return "manual";
  }
};

const getPumpModeColor = (theme: Theme, mode: ReducedPumpMode) => {
  switch (mode) {
    case "closed_loop":
      return theme.palette.blue.light;
    case "manual":
      return theme.palette.hyperglycemia.light;
  }
};

const getUserModeColor = (theme: Theme, mode: ClosedLoopUserMode) => {
  switch (mode) {
    case "normal":
      return undefined;
    case "exercising":
      return theme.palette.green.light;
    case "sleeping":
      return theme.palette.purple.light;
  }
};

const makeStyles = (theme: Theme) => ({
  container: css`
    stroke: currentColor;
  `,
  rect: css({
    borderRadius: theme.spacing(4),
  }),
});
